import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Building2, Leaf, Briefcase, Map } from "lucide-react";

const CSR = [
  { title: "Youngest fleet, lowest footprint", text: "A 2025–2026 fleet means modern engines, hybrids and EVs — cutting average fleet emissions year on year." },
  { title: "Road safety with the RTA", text: "We fund child-seat awareness campaigns and provide RTA-compliant seats on every booking that needs one." },
  { title: "Community mobility", text: "Discounted monthly rentals for verified frontline healthcare workers across the Northern Emirates." },
];

const SITEMAP = [
  ["Short-Term Rental", "/rent-a-car"], ["Monthly Rental", "/monthly"], ["International", "/international"],
  ["Our Fleet", "/fleet"], ["Our Network", "/network"], ["The Promise", "/promise"],
  ["Offers", "/offers"], ["Blog", "/blog"], ["Help & Support", "/help"],
  ["Partner With Us", "/partners"], ["Account / Vehicle", "/account"], ["Report an Accident", "/report-accident"],
];

export default function About() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 promo-stripe">
          <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="about-heading">{t("About Europcar Dubai & Northern Emirates")}</h1>
          <p className="text-white/80 text-sm mt-2 max-w-2xl">{t("Part of the Europcar Mobility Group's 140-country network, operated locally with a simple brief: make renting a car in the Emirates feel effortless.")}</p>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 w-full flex-1 space-y-12">
        <section className="grid md:grid-cols-2 gap-8 items-start" data-testid="about-story">
          <div>
            <div className="promo-stripe mb-4"><h2 className="heading-upper text-xl text-[#144733]">{t("Who we are")}</h2></div>
            <p className="text-sm text-[#57685e] leading-relaxed">
              {t("We run 14 branches across the UAE, a delivery operation covering all six emirates, and a wide fleet from city hatchbacks to the Land Cruiser. Our customers are residents on monthly plans, tourists arriving on a red-eye at Terminal 3, and businesses that need twenty cars by Thursday.")}
            </p>
            <p className="text-sm text-[#57685e] leading-relaxed mt-3">
              {t("Everything on this site — pricing, deposits, tolls, fines — is written the way we'd want it explained to us. That's the whole philosophy.")}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6" data-testid="about-egmg">
            <div className="flex items-center gap-2 mb-3"><Building2 className="w-5 h-5 text-[#009900]" /><h3 className="heading-upper text-sm text-[#144733]">{t("Europcar Mobility Group")}</h3></div>
            <p className="text-xs text-[#57685e] leading-relaxed">
              {t("Founded in Paris in 1949, Europcar Mobility Group is a global leader in mobility services with brands including Europcar, Goldcar and Fox Rent-a-Car — present in over 140 countries with a fleet exceeding 350,000 vehicles. The Dubai & Northern Emirates franchise brings that network's standards to the UAE.")}
            </p>
            <a href="https://egmg-premium.emergent.host/" target="_blank" rel="noreferrer"
              className="inline-block mt-2 text-xs font-semibold text-[#009900] underline underline-offset-2 hover:text-[#144733]" data-testid="about-egmg-link">
              {t("Visit the EGMG website →")}
            </a>
            <div className="flex items-center gap-2 mt-5 mb-2"><Briefcase className="w-5 h-5 text-[#009900]" /><h3 className="heading-upper text-sm text-[#144733]">{t("Careers")}</h3></div>
            <p className="text-xs text-[#57685e]">{t("We hire for branches, delivery and customer care across the emirates. Applications are handled on the EGMG careers portal —")} <a href="https://egmg-premium.emergent.host/careers#openings" target="_blank" rel="noreferrer" className="text-[#009900] underline" data-testid="about-careers-link">{t("view open positions")}</a>.</p>
          </div>
        </section>

        <section data-testid="about-csr">
          <div className="promo-stripe mb-5"><h2 className="heading-upper text-xl text-[#144733] flex items-center gap-2"><Leaf className="w-5 h-5 text-[#009900]" /> {t("Europcar Cares")}</h2></div>
          <div className="grid md:grid-cols-3 gap-5">
            {CSR.map((c) => (
              <div key={c.title} className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
                <h3 className="font-bold text-sm text-[#0F1E17] mb-2">{t(c.title)}</h3>
                <p className="text-xs text-[#57685e] leading-relaxed">{t(c.text)}</p>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="about-sitemap">
          <div className="promo-stripe mb-5"><h2 className="heading-upper text-xl text-[#144733] flex items-center gap-2"><Map className="w-5 h-5 text-[#009900]" /> {t("Sitemap")}</h2></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SITEMAP.map(([label, to]) => (
              <Link key={to} to={to} className="bg-white border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-xs font-medium text-[#144733] hover:border-[#009900] hover:text-[#009900] transition-colors">{t(label)}</Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
